import engineClient from '@/api/engineClient'
import type { Pagination } from '@/types'

export type EngineTagStatus = 'birthed' | 'tagged'

export type EngineTagRow = {
  rfid_code: string
  sku_code: string | null
  status: EngineTagStatus
  country_code: string
  month_year: string
  serial: string
  prefix?: string
  birthed_at: string | null
  tagged_at: string | null
  birthed_by: string | null
  tagged_by: string | null
  created_at?: string | null
  updated_at?: string | null
}

export type TagListFilters = {
  page?: number
  limit?: number
  status?: EngineTagStatus | ''
  sku_code?: string
  country_code?: string
  q?: string
}

export type TagListResult = {
  data: EngineTagRow[]
  pagination: Pagination
}

export type TagMapResult = {
  rfid_code: string
  sku_code: string
  status: 'tagged'
  country_code: string
  month_year: string
  serial: string
  tagged_at: string | null
  remapped?: boolean
  previous_sku_code?: string | null
  created?: boolean
}

export type TagMappingReportFilters = Omit<TagListFilters, 'page' | 'limit'>

const COUNTRY_LABEL: Record<string, string> = {
  '01': 'India',
  '02': 'UAE',
  '03': 'KSA',
}

const PAGE_LIMIT = 100
const MAX_PAGES = 500

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function buildTagMappingCsv(rows: EngineTagRow[]): string {
  const headers = [
    'RFID code',
    'Status',
    'SKU mapped',
    'SKU code',
    'Country code',
    'Country',
    'Month / year',
    'Serial',
    'Birthed at',
    'Tagged at',
    'Birthed by',
    'Tagged by',
  ]

  const lines = [headers.map(csvEscape).join(',')]
  for (const row of rows) {
    const sku = (row.sku_code ?? '').trim()
    const mapped = row.status === 'tagged' && sku ? 'Yes' : 'No'
    lines.push(
      [
        row.rfid_code,
        row.status,
        mapped,
        sku || '',
        row.country_code,
        COUNTRY_LABEL[row.country_code] ?? row.country_code,
        row.month_year,
        row.serial,
        row.birthed_at ?? '',
        row.tagged_at ?? '',
        row.birthed_by ?? '',
        row.tagged_by ?? '',
      ]
        .map((v) => csvEscape(String(v)))
        .join(',')
    )
  }
  return lines.join('\r\n')
}

export const engineService = {
  async listTags(filters: TagListFilters = {}): Promise<TagListResult> {
    const { data } = await engineClient.get<TagListResult>('/api/v1/tags', {
      params: {
        page: filters.page ?? 1,
        limit: filters.limit ?? 25,
        status: filters.status || undefined,
        sku_code: filters.sku_code || undefined,
        country_code: filters.country_code || undefined,
        q: filters.q || undefined,
      },
    })
    return data
  },

  /** Fetch all pages for report export (engine max page size = 100). */
  async listAllTags(filters: TagMappingReportFilters = {}): Promise<EngineTagRow[]> {
    const all: EngineTagRow[] = []
    let page = 1
    let totalPages = 1

    while (page <= totalPages && page <= MAX_PAGES) {
      const res = await engineService.listTags({
        ...filters,
        page,
        limit: PAGE_LIMIT,
      })
      all.push(...res.data)
      totalPages = Math.max(1, res.pagination.totalPages || 1)
      if (res.data.length === 0) break
      page += 1
    }

    return all
  },

  /**
   * Download RFID ↔ SKU mapping report (CSV, Excel-compatible) for current filters.
   * Columns: RFID, status, whether SKU is mapped, SKU, country, timestamps, actors.
   */
  async downloadTagMappingReport(
    filters: TagMappingReportFilters = {}
  ): Promise<{ filename: string; count: number }> {
    const rows = await engineService.listAllTags(filters)
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '_')
    const filename = `rfid_tag_mapping_report_${stamp}.csv`
    const csv = `\uFEFF${buildTagMappingCsv(rows)}`
    triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename)
    return { filename, count: rows.length }
  },

  /** Map or remap a birthed/tagged RFID to a SKU. */
  async tagOne(rfidCode: string, skuCode: string): Promise<TagMapResult> {
    const { data } = await engineClient.post<{ data: TagMapResult }>('/api/v1/tags/tag', {
      rfid_code: rfidCode.trim().toUpperCase(),
      sku_code: skuCode.trim(),
    })
    return data.data
  },
}
